import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/Badge'
import { MorphSelect } from '@/components/ui/MorphSelect'
import { LANGUAGE_OPTIONS } from '@/components/ui/languageOptions'
import { morphToast } from '@/components/ui/Toast'
import { Tooltip } from '@/components/ui/Tooltip'
import {
  LanguageIcon,
  LogoutIcon,
  SettingsIcon,
  ThemeIcon,
  UserIcon,
  WorkspacesIcon,
} from '@/components/ui/icons'
import { useAuthStore } from '@/stores/authStore'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { useSettingsStore } from '@/stores/settingsStore'
import { useConfirmStore } from '@/hooks/useConfirmStore'
import type { Locale } from '@/types'

export function UserMenu() {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const themePreference = useSettingsStore((state) => state.themePreference)
  const cycleThemePreference = useSettingsStore((state) => state.cycleThemePreference)
  const language = useSettingsStore((state) => state.language)
  const setLanguage = useSettingsStore((state) => state.setLanguage)
  const navigate = useNavigate()
  const { show } = useConfirmStore()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function escHandler(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('mousedown', handler)
    window.addEventListener('keydown', escHandler)
    return () => {
      window.removeEventListener('mousedown', handler)
      window.removeEventListener('keydown', escHandler)
    }
  }, [open])

  if (!user) return null

  function handleLogout() {
    setOpen(false)
    show(
      t('user.menu.logoutTitle', 'Sair da conta'),
      t('user.menu.logoutMessage', 'Tem certeza que deseja sair?'),
      () => { void logout().then(() => navigate('/login', { replace: true })) },
    )
  }

  function go(path: string) {
    setOpen(false)
    navigate(path)
  }

  function cycleTheme() {
    const next = themePreference === 'system'
      ? 'light'
      : themePreference === 'light' ? 'dark' : 'system'
    cycleThemePreference()
    morphToast.success(
      t(
        next === 'system'
          ? 'prompts.themeSystem'
          : next === 'light' ? 'prompts.themeLight' : 'prompts.themeDark',
      ),
    )
  }

  return (
    <div ref={containerRef} className="relative flex items-center shrink-0">
      <Tooltip content={user.name || user.email || ''} delayMs={400}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'transparent',
          border: '1px solid var(--border)',
          borderRadius: 999,
          padding: '3px 10px 3px 3px',
          cursor: 'pointer',
          color: 'var(--text-muted)',
          fontSize: 12,
          maxWidth: 220,
        }}
      >
        <UserAvatar userId={user.id} name={user.name} email={user.email} size={24} />
        <span
          style={{
            maxWidth: 140,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {user.name || user.email}
        </span>
        <span aria-hidden style={{ opacity: 0.7, fontSize: 10 }}>▾</span>
      </button>
      </Tooltip>

      {open ? (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 260,
            background: 'var(--bg-popup)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            boxShadow: 'var(--shadow-modal)',
            padding: 6,
            zIndex: 1000,
          }}
        >
          <MenuItem icon={<UserIcon />} onClick={() => go('/profile')}>
            {t('user.menu.profile', 'Perfil')}
          </MenuItem>
          <MenuItem icon={<WorkspacesIcon />} onClick={() => go('/workspaces')}>
            {t('user.menu.workspaces', 'Workspaces')}
          </MenuItem>
          {user.is_staff ? (
            <MenuItem icon={<SettingsIcon />} onClick={() => go('/admin')}>
              {t('admin.title', 'Administração')}
            </MenuItem>
          ) : null}

          <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />

          <MenuItem
            icon={<ThemeIcon />}
            onClick={cycleTheme}
            endSlot={(
              <Badge
                label={themePreference === 'system'
                  ? t('settingsMenu.themeSystem')
                  : themePreference === 'light' ? t('settingsMenu.themeLight') : t('settingsMenu.themeDark')}
                tone="neutral"
              />
            )}
          >
            {t('settingsMenu.theme')}
          </MenuItem>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              borderRadius: 6,
            }}
            onMouseEnter={(e) => {
              ;(e.currentTarget as HTMLDivElement).style.background = 'var(--sb-hover)'
            }}
            onMouseLeave={(e) => {
              ;(e.currentTarget as HTMLDivElement).style.background = 'transparent'
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: 'inline-flex',
                width: 14,
                height: 14,
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--accent)',
                flexShrink: 0,
              }}
            >
              <LanguageIcon />
            </span>
            <span style={{ flex: 1, fontSize: 13, color: 'var(--text)' }}>
              {t('footer.languageAria')}
            </span>
            <MorphSelect<Locale>
              value={language}
              ariaLabel={t('footer.languageAria')}
              options={LANGUAGE_OPTIONS}
              onChange={(nextLanguage) => { if (nextLanguage) setLanguage(nextLanguage) }}
              className="w-[126px]"
              minMenuWidth={138}
            />
          </div>

          <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />

          <MenuItem icon={<LogoutIcon />} onClick={handleLogout} danger>
            {t('user.menu.logout', 'Sair')}
          </MenuItem>
        </div>
      ) : null}
    </div>
  )
}

function MenuItem({
  children,
  onClick,
  icon,
  danger,
  endSlot,
}: {
  children: React.ReactNode
  onClick: () => void
  icon?: React.ReactNode
  danger?: boolean
  endSlot?: React.ReactNode
}) {
  return (
    <button
      role="menuitem"
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        gap: 10,
        textAlign: 'left',
        background: 'transparent',
        border: 'none',
        color: danger ? 'var(--color-danger)' : 'var(--text)',
        padding: '8px 10px',
        fontSize: 13,
        borderRadius: 6,
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        ;(e.currentTarget as HTMLButtonElement).style.background = 'var(--sb-hover)'
      }}
      onMouseLeave={(e) => {
        ;(e.currentTarget as HTMLButtonElement).style.background = 'transparent'
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          width: 14,
          height: 14,
          alignItems: 'center',
          justifyContent: 'center',
          color: danger ? 'var(--color-danger)' : 'var(--accent)',
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1 }}>{children}</span>
      {endSlot}
    </button>
  )
}

function MenuSectionLabel({
  children,
  icon,
}: {
  children: React.ReactNode
  icon?: React.ReactNode
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px 4px',
        fontSize: 11,
        color: 'var(--text-dim)',
      }}
    >
      <span aria-hidden="true" style={{ display: 'inline-flex', color: 'var(--accent)' }}>{icon}</span>
      <span>{children}</span>
    </div>
  )
}
